import classNames from "classnames";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  useTable,
  useSortBy,
  useExpanded,
  usePagination,
  useRowSelect,
  Column,
} from "react-table";

import { AdminLabOrderList, LabOrder } from "pwapi";

import Spinner from "components/Spinner";
import ApplicationState from "store/ApplicationState";

import OrdersColumns, {
  withCellDataAttrs,
  withOrderStatusRowProp,
} from "./OrdersColumns";
import OrdersFilters from "./OrdersFilters";
import OrdersTableView from "./OrdersTableView";
import AdminPagination from "../casesTable/Pagination";

const FilterableOrdersTable = ({
  columns,
}: {
  columns: Column<AdminLabOrderList | LabOrder>[];
}) => {
  const location = useLocation();
  const {
    results: orders,
    count,
    next,
    loading: isOrdersLoading,
  } = useSelector(({ admin }: ApplicationState) => admin.labOrders);
  const usp = new URLSearchParams(location.search);

  const initialParams = useMemo(() => {
    const sortBy = usp.get("sortBy");
    return {
      sortBy: sortBy
        ? sortBy.split(",").map((s) => ({
            id: s[0] === "-" ? s.slice(1) : s,
            desc: s[0] === "-",
          }))
        : [
            {
              id: "case_priority",
              desc: true,
            },
          ],
      pageIndex: parseInt(usp.get("page") || "1") - 1,
    };
  }, [location]);

  const setPageSizeToStorage = (value: string) => {
    localStorage.setItem("AdminOrdersPageSize", value);
  };

  const {
    headerGroups,
    rows,
    prepareRow,
    toggleAllRowsExpanded,

    // Pagination
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,

    state: { sortBy, pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data: orders || [],
      autoResetExpanded: false,
      manualSortBy: true,
      manualPagination: true,
      pageCount: -1,
      defaultColumn: {
        sortDescFirst: true,
      },
      initialState: {
        ...initialParams,
        pageSize: Number(
          JSON.parse(localStorage.getItem("AdminOrdersPageSize") || "25")
        ),
      },
    },
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect,
    withCellDataAttrs,
    withOrderStatusRowProp
  );

  const pageCount = count ? Math.ceil(count / pageSize) : -1;

  return (
    <div className="AdminCaselist">
      <OrdersFilters
        sortBy={sortBy}
        pageIndex={pageIndex}
        pageSize={pageSize}
        setPage={gotoPage}
        isLoading={!!isOrdersLoading}
        adminPaginationProps={{
          nextPage,
          previousPage,
          gotoPage,
          canPreviousPage,
          canNextPage: canNextPage && !!next,
          totalCount: count,

          pageIndex,
          pageCount,
          pageSize,
          setPageSize,

          toggleAllRowsExpanded,

          label: "orders",
          setToLocalStorage: setPageSizeToStorage,
          usePageSizeSelect: false,
          idSuffix: "upper",
        }}
      />
      <div
        className={classNames({ "AdminCaselist--loading": isOrdersLoading })}
      >
        {isOrdersLoading && (
          <div className="AdminCaselist__spinner">
            <Spinner />
          </div>
        )}
        <div className="adminList__container">
          <OrdersTableView
            headerGroups={headerGroups}
            rows={rows}
            prepareRow={prepareRow}
          />
        </div>
        <AdminPagination
          {...{
            nextPage,
            previousPage,
            gotoPage,
            canPreviousPage,
            canNextPage: canNextPage && !!next,
            totalCount: count,

            pageIndex,
            pageCount,
            pageSize,
            setPageSize,

            toggleAllRowsExpanded,

            label: "orders",
            setToLocalStorage: setPageSizeToStorage,
          }}
          pageSizes={[25, 100, 500, 1000]}
        />
      </div>
    </div>
  );
};

export default OrdersColumns(FilterableOrdersTable, {
  canOpenCase: true,
  hasFlagReason: true,
  canCompleteOrder: false,
  displayClipboardButton: false,
  printSlideFromOrderButton: false,
});
